import React, { type FC } from 'react';
import { Building2, CheckCircle2, Globe, MapPin } from 'lucide-react';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { safeGet } from './types';

const EXCLUDED_KEYS = new Set(['id', 'companyId', 'company_id', 'createdAt', 'updatedAt', 'userId']);

function formatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

function formatValue(val: unknown): string {
  if (val == null) return '';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

export const CompanyUpdateCard: FC<ToolCardProps> = ({ args, result, toolName }) => {
  // Try to show rich company data from result first
  const name = safeGet<string>(result, 'name');
  const tagline = safeGet<string>(result, 'tagline');
  const description = safeGet<string>(result, 'description');
  const location = safeGet<string>(result, 'location');
  const industry = safeGet<string>(result, 'industry');
  const employeeCount = safeGet<string>(result, 'employeeCount');
  const website = safeGet<string>(result, 'website');

  const hasRichData = name || tagline || description;

  if (hasRichData) {
    const metaParts = [location, industry, employeeCount].filter(Boolean);

    return (
      <CardShell>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle2 size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {toolName === 'companyCreate' ? 'Company created' : toolName === 'companyGet' ? 'Company' : 'Company updated'}
            </span>
          </div>

          {/* Company name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Building2 size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{name}</span>
          </div>

          {/* Tagline */}
          {tagline && (
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: '20px' }}>
              {tagline}
            </div>
          )}

          {/* Meta: location · industry · size */}
          {metaParts.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-tertiary)' }}>
              {location && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <MapPin size={12} /> {location}
                </span>
              )}
              {location && (industry || employeeCount) && <span style={{ opacity: 0.5 }}>&middot;</span>}
              {industry && <span>{industry}</span>}
              {industry && employeeCount && <span style={{ opacity: 0.5 }}>&middot;</span>}
              {employeeCount && <span>{employeeCount} employees</span>}
            </div>
          )}

          {/* Website */}
          {website && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <Globe size={12} style={{ color: 'var(--text-tertiary)' }} />
              <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{website}</span>
            </div>
          )}

          {/* Description */}
          {description && (
            <div style={{
              fontSize: 14, color: 'var(--text-secondary)', lineHeight: '20px',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {description}
            </div>
          )}
        </div>
      </CardShell>
    );
  }

  // Fallback: show args key-value pairs (when result is empty)
  const entries = Object.entries(args)
    .filter(([key]) => !EXCLUDED_KEYS.has(key))
    .slice(0, 4);

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Updated company</span>
        </div>
        {entries.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 24 }}>
            {entries.map(([key, val]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 13, lineHeight: '18px' }}>
                <span style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>{formatKey(key)}:</span>
                <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formatValue(val)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </CardShell>
  );
};
